import { reihenFarbe, useHinweis } from './diagramm'
import { Hinweis, HinweisZeile, Tipp } from './Hinweis'

export interface Anteil {
  name: string
  wert: number
  anzahl?: number
  farbe?: string
  tipp?: string
}

/**
 * Ein waagerechter Balken, in Anteile geteilt - Zahlarten, Warengruppen.
 *
 * Mit Legende UND unmittelbarer Beschriftung, solange es hoechstens vier
 * Anteile sind: Farbe allein darf die Zuordnung nie tragen. Zwischen den
 * Abschnitten liegen zwei Pixel Flaechenfarbe, sonst laesst sich die
 * Grenze nicht sehen.
 *
 * Ab dem neunten Anteil wird zusammengefasst statt eine neunte Farbe zu
 * erfinden - eine erzeugte Farbe waere weder unterscheidbar noch geprueft.
 */
export function Anteile({
  anteile, formatiere, hoehe = 34, anzahlName = 'Vorgänge',
}: {
  anteile: Anteil[]
  formatiere: (w: number) => string
  hoehe?: number
  /** Wie die Zahl in `anzahl` heißt. Zahlarten zählen Vorgänge,
   *  Speisen und Getränke zählen Positionen — und 2.469 Positionen als
   *  „Vorgänge" auszuweisen, wo es am selben Tag 876 gab, ist genau der
   *  Nennerfehler, den dieses Projekt schon zweimal hatte. */
  anzahlName?: string
}) {
  const h = useHinweis<Anteil & { quote: number }>()
  const sichtbar = anteile.slice(0, 8)
  const rest = anteile.slice(8)
  const liste = rest.length
    ? [...sichtbar, { name: 'Sonstige', wert: rest.reduce((s, a) => s + a.wert, 0) }]
    : sichtbar
  const gesamt = liste.reduce((s, a) => s + a.wert, 0) || 1

  return (
    <div>
      <div style={{
        display: 'flex', gap: 2, height: hoehe,
        borderRadius: 6, overflow: 'hidden',
      }}>
        {liste.map((a, i) => {
          const quote = (a.wert / gesamt) * 100
          return (
            <div key={a.name}
                 style={{
                   width: `${quote}%`,
                   background: a.farbe ?? reihenFarbe(i),
                   minWidth: 3,
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   cursor: 'default',
                 }}
                 onMouseMove={(e) => h.zeigen({ ...a, quote }, e)}
                 onMouseLeave={h.verbergen}>
              {quote >= 11 && (
                <span style={{
                  color: '#fff', fontSize: 12, fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: '0 1px 2px rgba(0,0,0,.35)',
                }}>
                  {quote.toFixed(0)} %
                </span>
              )}
            </div>
          )
        })}
      </div>

      <div className="legende" style={{ marginTop: 11 }}>
        {liste.map((a, i) => (
          <span className="eintrag" key={a.name}>
            <span className="klecks"
                  style={{ background: a.farbe ?? reihenFarbe(i) }} />
            {a.name}
            {a.tipp && <Tipp text={a.tipp} />}
            <span className="zahl" style={{ color: 'var(--schrift)', fontWeight: 500 }}>
              {formatiere(a.wert)}
            </span>
          </span>
        ))}
      </div>

      {h.inhalt && (
        <Hinweis lage={h.lage}>
          <div className="titel">{h.inhalt.name}</div>
          <HinweisZeile name="Umsatz" wert={formatiere(h.inhalt.wert)} />
          <HinweisZeile name="Anteil" wert={h.inhalt.quote.toFixed(1) + ' %'} />
          {h.inhalt.anzahl !== undefined && (
            <HinweisZeile name={anzahlName} wert={String(h.inhalt.anzahl)} />
          )}
        </Hinweis>
      )}
    </div>
  )
}
